<script lang="ts" setup>
import {
  CabinTypeLabel,
  CabinVariations,
  DrivetrainLabel,
  EngineTypeLabel,
  RoofTypeLabel,
  RoofVariations,
  SizeEngineVariations,
  SizeTypeLabel,
  SizeVariations,
  TechDataVariations,
  TechDataVehicle,
} from '@/types';

import {
  mapSizeTypeLabelToTranslation,
  mapRoofTypeLabelToTranslation,
  mapCabinTypeLabelToTranslation,
  mapDrivetrainTypeLabelToTranslation,
  mapEngineTypeLabelToTranslation,
} from '@/utils/translations';
import { getDefaultLocaleValue } from '../locales/default';
import { pushDataLayerEvent } from '@/utils/tracking';
import { getVehicleType } from '@/utils/vehicleType';
import { WbButtonGroup, WbButtonGroupItem } from '../../ssr/wb';

import { computed, ref, watch } from 'vue';

const props = defineProps<{
  sizes: SizeTypeLabel[];
  roofs: RoofTypeLabel[];
  cabins: CabinTypeLabel[];
  drivetrains: DrivetrainLabel[];
  batteryCapacities: number[];
  engines: EngineTypeLabel[];
  weights: number[];
  variations: TechDataVariations;
  vehicles: TechDataVehicle[];
  translations: Record<string, string>;
  modelSeries: string;
  selectedVariant?: TechDataVehicle;
}>();

const emit = defineEmits(['variant-selected']);

const vehicleType = getVehicleType(props.variations);

// Initialize with props.selectedVariant if provided
const selectedRoof = ref<RoofTypeLabel | null>(
  props.selectedVariant?.roofHeightType || null,
);
const selectedCabin = ref<CabinTypeLabel | null>(
  props.selectedVariant?.cabinType || null,
);
const selectedEngine = ref<EngineTypeLabel | null>(
  props.selectedVariant?.engineType || null,
);
const selectedBatteryCapacity = ref<number | null>(
  props.selectedVariant?.batteryCapacity || null,
);
const selectedWeight = ref<number | null>(
  props.selectedVariant?.weights?.weightMax?.value || null,
);
const selectedDrivetrain = ref<DrivetrainLabel | null>(
  props.selectedVariant?.drivetrainType || null,
);

// Set defaults if no selectedVariant is provided
if (!props.selectedVariant) {
  const firstVariation = props.variations.variations[0];
  selectedRoof.value =
    vehicleType === 'Sprinter_Roof'
      ? (firstVariation as RoofVariations).roofHeightTypes[0].roofHeightType
      : null;
  selectedCabin.value =
    vehicleType === 'Sprinter_Cabin'
      ? (firstVariation as CabinVariations).cabinTypes[0].cabinType
      : null;
  selectedEngine.value =
    vehicleType === 'Sprinter_Roof'
      ? (firstVariation as RoofVariations).roofHeightTypes[0].weightsMax[0]
          .drivetrainTypes[0].engineTypes[0].engineType
      : vehicleType === 'Sprinter_Cabin'
        ? (firstVariation as CabinVariations).cabinTypes[0].weightsMax[0]
            .drivetrainTypes[0].engineTypes[0].engineType
        : vehicleType === 'Citan' || vehicleType === 'Vito'
          ? props.engines[0]
          : null;
  selectedBatteryCapacity.value =
    vehicleType === 'Sprinter_Roof'
      ? (firstVariation as RoofVariations).roofHeightTypes[0].weightsMax[0]
          .drivetrainTypes[0].engineTypes[0].batteryCapacities?.[0]
      : vehicleType === 'Sprinter_Cabin'
        ? (firstVariation as CabinVariations).cabinTypes[0].weightsMax[0]
            .drivetrainTypes[0].engineTypes[0].batteryCapacities?.[0]
        : vehicleType === 'Citan' || vehicleType === 'Vito'
          ? (props.batteryCapacities[0] ?? null)
          : null;
  selectedWeight.value =
    vehicleType === 'Sprinter_Roof'
      ? (firstVariation as RoofVariations).roofHeightTypes[0].weightsMax[0]
          .weightMax
      : vehicleType === 'Sprinter_Cabin'
        ? (firstVariation as CabinVariations).cabinTypes[0].weightsMax[0]
            .weightMax
        : vehicleType === 'Vito'
          ? props.weights[0]
          : null;
  selectedDrivetrain.value =
    vehicleType === 'Sprinter_Roof'
      ? (firstVariation as RoofVariations).roofHeightTypes[0].weightsMax[0]
          .drivetrainTypes[0].drivetrainType
      : vehicleType === 'Sprinter_Cabin'
        ? (firstVariation as CabinVariations).cabinTypes[0].weightsMax[0]
            .drivetrainTypes[0].drivetrainType
        : props.drivetrains.length
          ? props.drivetrains[0]
          : null;
}

const vehicle = ref<TechDataVehicle | undefined>(props.selectedVariant);

const variation = ref<
  CabinVariations | RoofVariations | SizeEngineVariations | SizeVariations
>(props.variations.variations[0]);

// lists for disabling buttons
const available_weights = computed(() => {
  switch (true) {
    case vehicleType === 'Sprinter_Cabin':
      return (variation.value as CabinVariations).cabinTypes
        .filter((e) => e.cabinType === selectedCabin.value)
        .flatMap((e) =>
          e.weightsMax
            .filter((weightObj) =>
              weightObj.drivetrainTypes.some((drivetrain) =>
                drivetrain.engineTypes.some(
                  (engine) => engine.engineType === selectedEngine.value,
                ),
              ),
            )
            .map((weightObj) => weightObj.weightMax),
        );
    case vehicleType === 'Sprinter_Roof':
      return (variation.value as RoofVariations).roofHeightTypes
        .filter((e) => e.roofHeightType === selectedRoof.value)
        .flatMap((e) =>
          e.weightsMax
            .filter((weightObj) =>
              weightObj.drivetrainTypes.some((drivetrain) =>
                drivetrain.engineTypes.some(
                  (engine) => engine.engineType === selectedEngine.value,
                ),
              ),
            )
            .map((weightObj) => weightObj.weightMax),
        );
    case vehicleType === 'Vito' || vehicleType === 'Citan':
      if (!(variation.value as SizeVariations).weightsMax) return [];
      return (variation.value as SizeVariations).weightsMax
        .filter((weightObj) =>
          weightObj.drivetrainTypes.some((drivetrain) =>
            drivetrain.engineTypes.some(
              (engine) => engine.engineType === selectedEngine.value,
            ),
          ),
        )
        .map((weightObj) => weightObj.weightMax);
    default:
      return [];
  }
});

const available_cabins = computed(() => {
  if (vehicleType === 'Sprinter_Cabin') {
    return (
      (variation.value as CabinVariations).cabinTypes
        .filter((e) =>
          e.weightsMax.map((e) => e.weightMax).includes(selectedWeight.value!!),
        )
        .map((e) => e.cabinType) || []
    );
  } else {
    return [];
  }
});

const available_roofs = computed(() => {
  if (vehicleType === 'Sprinter_Roof') {
    return (
      (variation.value as RoofVariations).roofHeightTypes
        .filter((e) =>
          e.weightsMax.map((e) => e.weightMax).includes(selectedWeight.value!!),
        )
        .map((e) => e.roofHeightType) || []
    );
  } else {
    return [];
  }
});

const available_engines = computed(() => {
  switch (true) {
    case vehicleType === 'Sprinter_Cabin':
      return (variation.value as CabinVariations).cabinTypes
        .filter((e) => e.cabinType === selectedCabin.value)
        .flatMap((e) => e.weightsMax)
        .filter((weightObj) => weightObj.weightMax === selectedWeight.value)[0]
        .drivetrainTypes.map((e) => e.engineTypes)
        .flatMap((e) => e)
        .map((e) => e.engineType);
    case vehicleType === 'Sprinter_Roof':
      return (variation.value as RoofVariations).roofHeightTypes
        .filter((e) => e.roofHeightType === selectedRoof.value)
        .flatMap((e) => e.weightsMax)
        .filter((weightObj) => weightObj.weightMax === selectedWeight.value)[0]
        .drivetrainTypes.map((e) => e.engineTypes)
        .flatMap((e) => e)
        .map((e) => e.engineType);
    case vehicleType === 'Vito':
      return (variation.value as SizeVariations).weightsMax
        .filter((weightObj) => weightObj.weightMax === selectedWeight.value)[0]
        .drivetrainTypes.map((e) => e.engineTypes)
        .flatMap((e) => e)
        .map((e) => e.engineType);

    case vehicleType === 'Citan':
      return (variation.value as SizeEngineVariations).engineTypes.map(
        (e) => e.engineType,
      );
    default:
      return [];
  }
});

const available_batteryCapacities = computed<number[]>(() => {
  switch (vehicleType) {
    case 'Sprinter_Cabin':
      const selectedCabinType = (
        variation.value as CabinVariations
      ).cabinTypes.find((e) => e.cabinType === selectedCabin.value);

      if (!selectedCabinType) {
        return [];
      }

      const selectedCabinWeightObj = selectedCabinType.weightsMax.find(
        (weightObj) => weightObj.weightMax === selectedWeight.value,
      );

      if (!selectedCabinWeightObj) {
        return [];
      }

      const cabinBatteryCapacities = selectedCabinWeightObj.drivetrainTypes
        .flatMap((e) => e.engineTypes)
        .flatMap((e) => e.batteryCapacities);

      return cabinBatteryCapacities;
    case 'Sprinter_Roof':
      const selectedRoofType = (
        variation.value as RoofVariations
      ).roofHeightTypes.find((e) => e.roofHeightType === selectedRoof.value);

      if (!selectedRoofType) {
        return [];
      }

      const selectedRoofWeightObj = selectedRoofType.weightsMax.find(
        (weightObj) => weightObj.weightMax === selectedWeight.value,
      );

      if (!selectedRoofWeightObj) {
        return [];
      }

      const roofBatteryCapacities = selectedRoofWeightObj.drivetrainTypes
        .flatMap((e) => e.engineTypes)
        .flatMap((e) => e.batteryCapacities);

      return roofBatteryCapacities;
    case 'Vito':
      return (variation.value as SizeVariations).weightsMax.flatMap((e) =>
        e.drivetrainTypes
          .flatMap((e) => e.engineTypes)
          .flatMap((e) => e.batteryCapacities ?? []),
      );
    case 'Citan':
      return (variation.value as SizeEngineVariations).engineTypes.flatMap(
        (e) => e.batteryCapacities ?? [],
      );
    default:
      return [];
  }
});

const available_drivetrains = computed(() => {
  switch (true) {
    case vehicleType === 'Sprinter_Cabin':
      return (variation.value as CabinVariations).cabinTypes
        .filter((e) => e.cabinType === selectedCabin.value)
        .flatMap((e) => e.weightsMax)
        .filter((weightObj) => weightObj.weightMax === selectedWeight.value)[0]
        .drivetrainTypes.map((e) => e.drivetrainType);
    case vehicleType === 'Sprinter_Roof':
      return (variation.value as RoofVariations).roofHeightTypes
        .filter((e) => e.roofHeightType === selectedRoof.value)
        .flatMap((e) => e.weightsMax)
        .filter((weightObj) => weightObj.weightMax === selectedWeight.value)[0]
        .drivetrainTypes.map((e) => e.drivetrainType);
    case vehicleType === 'Vito' || vehicleType === 'Citan':
      if (!(variation.value as SizeVariations).weightsMax) return [];
      return (variation.value as SizeVariations).weightsMax.flatMap((e) =>
        e.drivetrainTypes.map((e) => e.drivetrainType),
      );
    default:
      return [];
  }
});

watch(
  variation,
  () => {
    setVehiclePropertiesBySize(variation.value);
    selectVehicle();
  },
  { immediate: !props.selectedVariant }, // Skip immediate if selectedVariant is provided
);

watch(
  () => props.selectedVariant,
  (newVariant) => {
    if (newVariant) {
      selectedRoof.value = newVariant.roofHeightType || null;
      selectedCabin.value = newVariant.cabinType || null;
      selectedEngine.value = newVariant.engineType || null;
      selectedBatteryCapacity.value = newVariant.batteryCapacity || null;
      selectedWeight.value = newVariant.weights?.weightMax?.value || null;
      selectedDrivetrain.value = newVariant.drivetrainType || null;
      vehicle.value = newVariant;
    }
  },
);

// tracking
function trackFeatureSelection(label: string, vehicle: TechDataVehicle | null) {
  if (!vehicle) {
    console.warn('No vehicle selected for tracking.');
    return;
  }
  const formattedLabel = label.toLowerCase();

  pushDataLayerEvent({
    name: 'feature',
    category: 'feature',
    action: 'cvans_techdata_component',
    label: formattedLabel,
    vehicle: {
      modelCode: props.modelSeries,
      class: vehicle.vehicleClass,
      bodyType: vehicle.bodyType,
      baumusterId: vehicle.baumuster,
      type: 'van',
      category: 'commercial',
    },
  });
}

// functions for buttons
function selectCabin(cabin: CabinTypeLabel | null) {
  selectedCabin.value = cabin;
  changeProperties(cabin);
  selectVehicle();
}

function selectRoof(roof: RoofTypeLabel | null) {
  selectedRoof.value = roof;
  changeProperties(roof);
  selectVehicle();
}

function selectEngine(engine: EngineTypeLabel | null) {
  selectedEngine.value = engine;
  changeProperties(engine);
  selectVehicle();
}

function selectBatteryCapacity(batteryCapacity: number | null) {
  selectedBatteryCapacity.value = batteryCapacity;
  changeProperties(batteryCapacity);
  selectVehicle();
}

function selectWeight(weight: number | null) {
  selectedWeight.value = weight;
  if (shouldChangeDrivetrain()) {
    selectDrivetrain(available_drivetrains.value[0]);
  }
  selectVehicle();
}

function selectDrivetrain(drivetrain: DrivetrainLabel | null) {
  selectedDrivetrain.value = drivetrain;
  selectVehicle();
}

// setting vehicle properties from a selected variation
function setVehiclePropertiesBySize(
  selectedVariation:
    | CabinVariations
    | RoofVariations
    | SizeEngineVariations
    | SizeVariations,
) {
  // Set the new variation, i.e., vehicle size.
  variation.value = selectedVariation;

  switch (true) {
    case vehicleType === 'Citan':
      if (shouldEngineChange()) selectEngine(available_engines.value[0]);
      break;
    case vehicleType === 'Vito':
      selectWeight(
        (selectedVariation as SizeVariations).weightsMax[0].weightMax,
      );
      if (shouldChangeDrivetrain()) {
        selectDrivetrain(available_drivetrains.value[0]);
      }
      if (shouldEngineChange()) {
        selectEngine(available_engines.value[0]);
      }
      if (shouldBatteryCapacityChange()) {
        selectBatteryCapacity(available_batteryCapacities.value[0]);
      }
      break;
    case vehicleType === 'Sprinter_Cabin':
      if (shouldCabinChange()) {
        selectCabin(
          (selectedVariation as CabinVariations).cabinTypes[0].cabinType,
        );
      }
      selectEngine(
        (selectedVariation as CabinVariations).cabinTypes[0].weightsMax[0]
          .drivetrainTypes[0].engineTypes[0].engineType,
      );
      break;
    case vehicleType === 'Sprinter_Roof':
      if (shouldRoofChange()) {
        selectRoof(
          (selectedVariation as RoofVariations).roofHeightTypes[0]
            .roofHeightType,
        );
      }
      selectEngine(
        (selectedVariation as RoofVariations).roofHeightTypes[0].weightsMax[0]
          .drivetrainTypes[0].engineTypes[0].engineType,
      );
      break;
    default:
      break;
  }
}

// selecting a vehicle based on properties
function selectVehicle() {
  if (vehicleType === 'Citan') {
    vehicle.value = props.vehicles.find(
      (e) =>
        e.sizeType === variation.value.sizeType &&
        e.engineType === selectedEngine.value &&
        (selectedBatteryCapacity.value === undefined ||
          e.batteryCapacity === selectedBatteryCapacity.value),
    );
  }
  if (vehicleType === 'Vito') {
    vehicle.value = props.vehicles.find(
      (e) =>
        e.sizeType === variation.value.sizeType &&
        e.weights?.weightMax?.value === selectedWeight.value &&
        e.drivetrainType === selectedDrivetrain.value &&
        e.engineType === selectedEngine.value &&
        (selectedBatteryCapacity.value === undefined ||
          e.batteryCapacity === selectedBatteryCapacity.value),
    );
  }
  if (vehicleType === 'Sprinter_Cabin' || vehicleType === 'Sprinter_Roof') {
    vehicle.value = props.vehicles.find(
      (e) =>
        e.sizeType === variation.value.sizeType &&
        e.weights?.weightMax?.value === selectedWeight.value &&
        e.drivetrainType === selectedDrivetrain.value &&
        e.cabinType === selectedCabin.value &&
        e.roofHeightType === selectedRoof.value &&
        (selectedBatteryCapacity.value === undefined ||
          e.batteryCapacity === selectedBatteryCapacity.value),
    );
  }
  emit('variant-selected', vehicle.value);
}

// misc. functions
function changeProperties(
  label: CabinTypeLabel | RoofTypeLabel | EngineTypeLabel | number | null,
) {
  if (!label) return;
  if (shouldWeightMaxChange()) {
    let weightMax = available_weights.value[0];
    if (
      typeof label === 'string' &&
      Object.keys(CabinTypeLabel).includes(label)
    ) {
      weightMax = (variation.value as CabinVariations).cabinTypes[0]
        .weightsMax[0].weightMax;
    }

    if (
      typeof label === 'string' &&
      Object.keys(RoofTypeLabel).includes(label)
    ) {
      (variation.value as RoofVariations).roofHeightTypes[0].weightsMax[0];
    }

    selectWeight(weightMax);
  }
  if (shouldChangeDrivetrain()) {
    selectDrivetrain(available_drivetrains.value[0]);
  }
  if (shouldEngineChange()) {
    selectEngine(available_engines.value[0]);
  }
  if (shouldBatteryCapacityChange()) {
    selectBatteryCapacity(available_batteryCapacities.value[0]);
  }
}

function shouldRoofChange() {
  return (
    selectedRoof.value && !available_roofs.value.includes(selectedRoof.value)
  );
}

function shouldCabinChange() {
  return (
    selectedCabin.value && !available_cabins.value.includes(selectedCabin.value)
  );
}

function shouldWeightMaxChange() {
  return (
    selectedWeight.value &&
    !available_weights.value.includes(selectedWeight.value)
  );
}

function shouldChangeDrivetrain() {
  return (
    selectedDrivetrain.value &&
    !available_drivetrains.value.includes(selectedDrivetrain.value)
  );
}

function shouldEngineChange() {
  return (
    selectedEngine.value &&
    !available_engines.value.includes(selectedEngine.value)
  );
}

function shouldBatteryCapacityChange() {
  return (
    selectedBatteryCapacity.value &&
    !available_batteryCapacities.value.includes(selectedBatteryCapacity.value)
  );
}
</script>
<template>
  <div class="owvans-technical-data-wrapper">
    <div class="wb-grid-container wb-grid-container--full-width">
      <div class="wb-grid-row">
        <div
          class="wb-grid-col variant-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="variations.variations.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_sizeTypeLabel ||
                getDefaultLocaleValue('sizeTypeLabel')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(variation, index) in variations.variations"
                  :key="`size-button${index}`"
                  variant="secondary"
                  :selected="
                    variation.sizeType === vehicle?.sizeType || undefined
                  "
                  :data-test-id="`owvans-tech-data-variations-desktop-${variation.sizeType}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        variation.sizeType === vehicle?.sizeType,
                    },
                  }"
                  @click="
                    (setVehiclePropertiesBySize(variation),
                    trackFeatureSelection(
                      `size_type.${variation.sizeType}`,
                      vehicle ?? null,
                    ))
                  "
                >
                  {{
                    props.translations[
                      mapSizeTypeLabelToTranslation(variation.sizeType)
                    ] ||
                    getDefaultLocaleValue(
                      `sizeType_${variation.sizeType.toLocaleLowerCase()}`,
                    )
                  }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-grid-col roof-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="roofs.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_roofTypeLabel ||
                getDefaultLocaleValue('roofTypeLabel')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(roof, index) in roofs"
                  :key="index"
                  variant="secondary"
                  :selected="roof === vehicle?.roofHeightType || undefined"
                  :disabled="!available_roofs.includes(roof) || undefined"
                  :data-test-id="`owvans-tech-data-roofs-desktop-${roof}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': roof === vehicle?.roofHeightType,
                      'wbx-button--disabled': !available_roofs.includes(roof),
                    },
                  }"
                  @click="
                    (selectRoof(roof),
                    trackFeatureSelection(`roof_type.${roof}`, vehicle ?? null))
                  "
                >
                  {{
                    props.translations[mapRoofTypeLabelToTranslation(roof)] ||
                    getDefaultLocaleValue(
                      `roofType_${roof.toLocaleLowerCase()}`,
                    )
                  }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-grid-col roof-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="cabins.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_cabinTypeLabel ||
                getDefaultLocaleValue('cabinTypeLabel')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(cabin, index) in cabins"
                  :key="index"
                  variant="secondary"
                  :selected="cabin === vehicle?.cabinType || undefined"
                  :disabled="!available_cabins.includes(cabin) || undefined"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': cabin === vehicle?.cabinType,
                      'wbx-button--disabled': !available_cabins.includes(cabin),
                    },
                  }"
                  :data-test-id="`owvans-tech-data-cabins-desktop-${cabin}`"
                  @click="
                    (selectCabin(cabin),
                    trackFeatureSelection(
                      `cabin_type.${cabin}`,
                      vehicle ?? null,
                    ))
                  "
                >
                  {{
                    props.translations[mapCabinTypeLabelToTranslation(cabin)] ||
                    getDefaultLocaleValue(
                      `cabinType_${cabin.toLocaleLowerCase()}`,
                    )
                  }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-margin-bottom-3xs weight-selector wb-grid-col wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="engines.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_engine ||
                getDefaultLocaleValue('engine')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(engine, index) in engines"
                  :key="index"
                  variant="secondary"
                  :selected="engine === vehicle?.engineType || undefined"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': engine === vehicle?.engineType,
                      'wbx-button--disabled':
                        !available_engines.includes(engine),
                    },
                  }"
                  :disabled="!available_engines.includes(engine) || undefined"
                  :data-test-id="`owvans-tech-data-engines-desktop-${engine}`"
                  @click="
                    (selectEngine(engine),
                    trackFeatureSelection(
                      `engine_type.${engine}`,
                      vehicle ?? null,
                    ))
                  "
                >
                  {{
                    props.translations[
                      mapEngineTypeLabelToTranslation(engine)
                    ] ||
                    getDefaultLocaleValue(
                      `engineType_${engine.toLocaleLowerCase()}`,
                    )
                  }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-grid-col wb-margin-bottom-3xs batterycapacity-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="batteryCapacities.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_batteryCapacity ||
                getDefaultLocaleValue('batteryCapacity')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(batteryCapacity, index) in batteryCapacities"
                  :key="index"
                  variant="secondary"
                  :selected="
                    batteryCapacity === vehicle?.batteryCapacity || undefined
                  "
                  :disabled="
                    !available_batteryCapacities.includes(batteryCapacity) ||
                    undefined
                  "
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        batteryCapacity === vehicle?.batteryCapacity,
                      'wbx-button--disabled':
                        !available_batteryCapacities.includes(batteryCapacity),
                    },
                  }"
                  @click="
                    (selectBatteryCapacity(batteryCapacity),
                    trackFeatureSelection(
                      `battery_capacity.${batteryCapacity}`,
                      vehicle ?? null,
                    ))
                  "
                  :data-test-id="`owvans-tech-data-batteryCapacity-desktop-${batteryCapacity}`"
                >
                  {{ batteryCapacity }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-grid-col wb-margin-bottom-3xs weight-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="weights.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                translations.owvc_tech_data_weight ||
                getDefaultLocaleValue('weight')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(weight, index) in weights"
                  :key="index"
                  variant="secondary"
                  :selected="
                    weight === vehicle?.weights?.weightMax?.value || undefined
                  "
                  :disabled="!available_weights.includes(weight) || undefined"
                  :data-test-id="`owvans-tech-data-weights-desktop-${weight}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        weight === vehicle?.weights?.weightMax?.value,
                      'wbx-button--disabled':
                        !available_weights.includes(weight),
                    },
                  }"
                  @click="
                    (selectWeight(weight),
                    trackFeatureSelection(`weight.${weight}`, vehicle ?? null))
                  "
                >
                  {{ weight }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
        <div
          class="wb-grid-col wb-margin-bottom-3xs weight-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="drivetrains.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text span-s">
              {{
                translations.owvc_tech_data_drivetrainType ||
                getDefaultLocaleValue('drivetrainType')
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{
                  class: 'wbx-button-group',
                }"
              >
                <WbButtonGroupItem
                  v-for="(train, index) in drivetrains"
                  :key="index"
                  variant="secondary"
                  :selected="train === vehicle?.drivetrainType || undefined"
                  :disabled="
                    !available_drivetrains.includes(train) || undefined
                  "
                  :data-test-id="`owvans-tech-data-drivetrains-desktop-${train}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': train === vehicle?.drivetrainType,
                      'wbx-button--disabled':
                        !available_drivetrains.includes(train),
                    },
                  }"
                  @click="
                    (selectDrivetrain(train),
                    trackFeatureSelection(
                      `drivetrain.${train}`,
                      vehicle ?? null,
                    ))
                  "
                >
                  {{
                    props.translations[
                      mapDrivetrainTypeLabelToTranslation(train)
                    ] ||
                    getDefaultLocaleValue(
                      `drivetrain_${train.toLocaleLowerCase()}`,
                    )
                  }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
.owvans-technical-data-wrapper {
  &__column-text {
    color: var(--wb-grey-20);
    display: -ms-flexbox;
    font-family: var(--wb-font-text);
    font-size: 1rem;
    font-style: normal;
    font-weight: 700;
  }
  .wb-grid-row {
    grid-gap: 16px;
  }

  .wb-grid-col {
    width: 100%;
  }
}
.owvans-technical-data-wrapper__button-group {
  @include wb-spacing('margin-top', 'xxs');
  overflow-y: hidden;
}
.wbx-button-group,
.wb-button-group {
  width: 100%;
  white-space: nowrap;

  .wbx-button-group-item,
  .wb-button-group-item {
    flex-grow: 1;
    flex-shrink: 1;
  }
}
</style>
